import { useState } from "react";
import { useRoute } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { useToast } from "@/hooks/use-toast";
import { PoweredByAttribution } from "@/components/powered-by-attribution";
import {
  Loader2,
  Calendar,
  FileText,
  CreditCard,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  Send,
  ExternalLink,
  FileSignature,
  Building2,
} from "lucide-react";
import { format } from "date-fns";

interface PortalData {
  client: {
    id: string;
    accountName: string;
    contactName: string | null;
    contactEmail: string | null;
  };
  branding: {
    workspaceName: string;
    primaryColor: string;
    logoUrl: string | null;
    showPoweredByAttribution?: boolean;
  };
  bookings: Array<{
    id: string;
    showName: string;
    showDate: string | null;
    status: string;
  }>;
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    total: string | number | null;
    balanceDue: string | number | null;
    status: string;
    invoiceDate: string | null;
    dueDate: string | null;
    publicSlug: string | null;
  }>;
  documents: Array<{
    id: string;
    displayName: string;
    status: string;
    signatureMethod: string | null;
    signingToken: string | null;
    signedCompletedAt: string | null;
    sentAt: string | null;
  }>;
  messages: Array<{
    id: string;
    senderType: "client" | "rep";
    senderName: string | null;
    body: string;
    createdAt: string;
  }>;
}

export default function PortalPage() {
  const [, params] = useRoute("/portal/:token");
  const token = params?.token || "";
  const { toast } = useToast();
  const [messageDraft, setMessageDraft] = useState("");

  const { data, isLoading, error } = useQuery<PortalData>({
    queryKey: ["/api/portal", token],
    queryFn: async () => {
      const res = await fetch(`/api/portal/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Unable to load portal");
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  const sendMessageMutation = useMutation({
    mutationFn: (body: string) =>
      apiRequest("POST", `/api/portal/${token}/messages`, {
        body,
        senderName: data?.client.contactName,
        senderEmail: data?.client.contactEmail,
      }),
    onSuccess: () => {
      setMessageDraft("");
      queryClient.invalidateQueries({ queryKey: ["/api/portal", token] });
      toast({ title: "Message sent", description: "Your team will reply by email." });
    },
    onError: () => toast({ title: "Failed to send message", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    // The default fetcher throws `${status}: ${text}` — try to extract the
    // server's JSON message + portalDisabled flag so we can render a clean
    // friendly state for the workspace-master-switch-off case instead of a
    // raw "410: {...}" string.
    let friendlyMessage = "This portal link is not valid.";
    let portalDisabled = false;
    if (error instanceof Error) {
      const raw = error.message || "";
      const colonIdx = raw.indexOf(":");
      const bodyPart = colonIdx >= 0 ? raw.slice(colonIdx + 1).trim() : raw;
      try {
        const parsed = JSON.parse(bodyPart);
        if (parsed && typeof parsed.message === "string") friendlyMessage = parsed.message;
        if (parsed && parsed.portalDisabled === true) portalDisabled = true;
      } catch {
        if (raw) friendlyMessage = raw;
      }
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div
              className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                portalDisabled ? "bg-muted" : "bg-destructive/10"
              }`}
            >
              {portalDisabled ? (
                <Building2 className="w-6 h-6 text-muted-foreground" />
              ) : (
                <AlertCircle className="w-6 h-6 text-destructive" />
              )}
            </div>
            <CardTitle data-testid="text-portal-error-title">
              {portalDisabled ? "Your booking team handles this directly" : "Portal unavailable"}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            <p data-testid="text-portal-error-message">{friendlyMessage}</p>
            {!portalDisabled && (
              <p className="mt-3">If you think this is a mistake, please contact your booking team for a fresh link.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, branding, bookings, invoices, documents, messages } = data;
  const accentStyle = { borderTopColor: branding.primaryColor, borderTopWidth: 4 };
  const pendingDocs = documents.filter((d) => d.status === "sent");
  const completedDocs = documents.filter((d) => d.status === "completed");
  const openInvoices = invoices.filter(
    (i) => i.status !== "paid" && i.status !== "void" && Number(i.balanceDue ?? 0) > 0,
  );
  const closedInvoices = invoices.filter((i) => !openInvoices.includes(i));

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Branded header */}
      <header className="bg-background border-b border-border" style={accentStyle}>
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={branding.workspaceName}
                className="h-10 w-auto max-w-[180px] object-contain"
                data-testid="img-portal-logo"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Client Portal</p>
              <p className="font-semibold truncate" data-testid="text-portal-workspace">
                {branding.workspaceName}
              </p>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-xs text-muted-foreground">Welcome</p>
            <p className="text-sm font-medium truncate max-w-[220px]" data-testid="text-portal-client-name">
              {client.contactName || client.accountName}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Bookings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4" />
              Upcoming bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-no-bookings">
                No upcoming bookings yet.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-3 py-3"
                    data-testid={`row-booking-${b.id}`}
                  >
                    <div>
                      <p className="font-medium">{b.showName}</p>
                      {b.showDate && (
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(b.showDate), "EEEE, MMMM d, yyyy")}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="capitalize text-xs">
                      {b.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoices */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="w-4 h-4" />
              Invoices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {openInvoices.length === 0 && closedInvoices.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-no-invoices">
                No invoices on file yet.
              </p>
            ) : (
              <>
                {openInvoices.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Pay now</p>
                    {openInvoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-card"
                        data-testid={`row-invoice-${inv.id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-medium">Invoice {inv.invoiceNumber}</p>
                          <p className="text-sm text-muted-foreground">
                            {inv.dueDate ? `Due ${format(new Date(inv.dueDate), "MMM d, yyyy")}` : "No due date"}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold tabular-nums">
                            ${Number(inv.balanceDue ?? inv.total ?? 0).toLocaleString()}
                          </p>
                          {inv.publicSlug && (
                            <Button
                              size="sm"
                              className="mt-1 h-7"
                              onClick={() => window.open(`/invoice/${inv.publicSlug}`, "_blank")}
                              data-testid={`button-pay-invoice-${inv.id}`}
                            >
                              Pay
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {closedInvoices.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">History</p>
                    {closedInvoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between gap-3 py-2 text-sm"
                        data-testid={`row-invoice-history-${inv.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span>Invoice {inv.invoiceNumber}</span>
                          {inv.invoiceDate && (
                            <span className="text-muted-foreground">
                              · {format(new Date(inv.invoiceDate), "MMM d, yyyy")}
                            </span>
                          )}
                        </div>
                        <span className="text-muted-foreground tabular-nums">
                          ${Number(inv.total ?? 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4" />
              Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingDocs.length === 0 && completedDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-no-docs">
                No documents yet.
              </p>
            ) : (
              <>
                {pendingDocs.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Action needed</p>
                    {pendingDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-card"
                        data-testid={`row-doc-${doc.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{doc.displayName}</p>
                            {doc.sentAt && (
                              <p className="text-xs text-muted-foreground">
                                Sent {format(new Date(doc.sentAt), "MMM d, yyyy")}
                              </p>
                            )}
                          </div>
                        </div>
                        {doc.signingToken ? (
                          <Button
                            size="sm"
                            onClick={() => window.open(`/sign/${doc.signingToken}`, "_blank")}
                            data-testid={`button-sign-doc-${doc.id}`}
                          >
                            <FileSignature className="w-4 h-4 mr-2" />
                            Sign
                          </Button>
                        ) : (
                          <Badge variant="outline" className="text-xs">Awaiting</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {completedDocs.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Completed</p>
                    {completedDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between gap-3 py-2 text-sm"
                        data-testid={`row-doc-completed-${doc.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span>{doc.displayName}</span>
                        </div>
                        {doc.signedCompletedAt && (
                          <span className="text-muted-foreground">
                            {format(new Date(doc.signedCompletedAt), "MMM d, yyyy")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Messages */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageCircle className="w-4 h-4" />
              Message your team
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {messages.length > 0 && (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`p-3 rounded-lg text-sm ${
                      m.senderType === "client"
                        ? "bg-primary/5 border border-primary/20"
                        : "bg-muted"
                    }`}
                    data-testid={`message-${m.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1 text-xs text-muted-foreground">
                      <span className="font-medium">
                        {m.senderType === "client" ? m.senderName || "You" : `${branding.workspaceName} team`}
                      </span>
                      <span>{format(new Date(m.createdAt), "MMM d, h:mm a")}</span>
                    </div>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>
            )}
            <Textarea
              value={messageDraft}
              onChange={(e) => setMessageDraft(e.target.value)}
              placeholder="Type a message to your booking team..."
              className="min-h-[100px]"
              data-testid="input-portal-message"
            />
            <div className="flex justify-end">
              <Button
                onClick={() => messageDraft.trim() && sendMessageMutation.mutate(messageDraft.trim())}
                disabled={!messageDraft.trim() || sendMessageMutation.isPending}
                data-testid="button-send-message"
              >
                {sendMessageMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send message
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-muted-foreground py-4">
          <p data-testid="text-portal-workspace-name">
            Booking team: {branding.workspaceName}
          </p>
          <PoweredByAttribution
            show={branding.showPoweredByAttribution ?? true}
            className="text-xs text-muted-foreground/70 tracking-wide mt-2"
          />
        </div>
      </main>
    </div>
  );
}
